// Staleness per hex: how long since any dated fact touched the hex (ARCHITECTURE §1.7, fog of war).
//
//   age = D − newest fact date ≤ D        (days)
//   age ≤ 60        → 0     fully fresh
//   60 < age < 365  → 1..254, linear
//   age ≥ 365, none → 255   parchment
//
// Facts come in two reaches:
//
//   country facts  → every hex the country owns at D (default worldview)
//                    war start / key developments / status and intensity checks, for each theatre
//                    state; leader terms; memberships; sanctions (both sides); treaties (parties);
//                    `published` dates of the sources those records cite; Current events bullets
//                    tagged with the country
//   hex facts      → that hex only
//                    control episode start/end over the episode's hexes; the latest control-series
//                    snapshot ≤ D over its hexes; Current events bullets located in the hex
//
// A hex whose control is unknown (BASIS.UNKNOWN, inside a declared gap of a control series) is
// fogged at full staleness by `fogUnknown`, whatever the country facts say: a fresh war headline
// does not say who holds it.
//
// Current events arrive per year, lazily (World.events loads them). Until a year is loaded its
// bullets do not count, so `version` bumps on every ingest and callers key caches on it.

import { HEX_COUNT } from '../core/grid.ts';
import type { ActorId, DateStr, Source, SourceId, WorldEvent, WorldState } from '../core/schema.ts';
import type { ActorTable } from './actors.ts';
import { BASIS, type ControlModel } from './control.ts';
import { dayOf, lastAtMost } from './util.ts';

/** ARCHITECTURE §1.3: a status claim up to 60 days old is current. */
export const FRESH_DAYS = 60;

/** A year without a source is fully fogged. */
export const STALE_DAYS = 365;

const MAX_LEVEL = 255;

/** "No fact yet" as a day number: older than any real date. */
const NEVER = -1_000_000_000;

export interface StaleModel {
  maxCountry: number;
  /** Country id → fact days (unsorted while `dirty`). */
  lists: number[][];
  /** Country id → ascending fact days. */
  sorted: Int32Array[];
  /** Hex facts encoded as day * HEX_COUNT + hex, ascending once sorted. */
  hexKeys: number[];
  hexSorted: Float64Array;
  dirty: boolean;
  /** Control series: snapshot day numbers and hexes, per series. */
  series: Array<{ days: Int32Array; hexes: Int32Array[] }>;
  /** Scratch per-hex newest day. Internal only, never returned, so reusing it is safe. */
  scratch: Int32Array;
  /** Bumps whenever facts are added. */
  version: number;
  /** age in days → level, for 0..STALE_DAYS. */
  lut: Uint8Array;
}

export interface StaleInputs {
  world: WorldState;
  actors: ActorTable;
  sources: Map<SourceId, Source>;
  control: ControlModel;
}

/** Age in days → 0..255 (see the header). */
export function ageLevel(age: number): number {
  if (age <= FRESH_DAYS) {
    return 0;
  }
  if (age >= STALE_DAYS) {
    return MAX_LEVEL;
  }
  return Math.round((MAX_LEVEL * (age - FRESH_DAYS)) / (STALE_DAYS - FRESH_DAYS));
}

function levelTable(): Uint8Array {
  const lut = new Uint8Array(STALE_DAYS + 1);
  for (let a = 0; a <= STALE_DAYS; a++) {
    lut[a] = ageLevel(a);
  }
  return lut;
}

// ─── collecting facts ────────────────────────────────────────────────────────────────────

interface Sink {
  add(id: ActorId, date: DateStr | null | undefined): void;
  addSources(ids: ActorId[], from: DateStr, sources: SourceId[]): void;
}

function sink(m: StaleModel, actors: ActorTable, sources: Map<SourceId, Source>): Sink {
  const add = (id: ActorId, date: DateStr | null | undefined) => {
    const ix = actors.byId.get(id);
    if (!date || ix === undefined || ix > m.maxCountry) {
      return;
    }
    m.lists[ix].push(dayOf(date));
  };

  // A source published on or after the record began is evidence the record still held then.
  const addSources = (ids: ActorId[], from: DateStr, cited: SourceId[]) => {
    for (const sid of cited) {
      const pub = sources.get(sid)?.published;
      if (!pub || pub < from) {
        continue;
      }
      for (const id of ids) {
        add(id, pub);
      }
    }
  };
  return { add, addSources };
}

function spanFacts(s: Sink, ids: ActorId[], span: { from: DateStr; to: DateStr | null; sources: SourceId[] }): void {
  for (const id of ids) {
    s.add(id, span.from);
    s.add(id, span.to);
  }
  s.addSources(ids, span.from, span.sources);
}

function worldFacts(s: Sink, w: WorldState): void {
  for (const war of w.wars) {
    spanFacts(s, war.theatre, war);
    for (const id of war.theatre) {
      s.add(id, war.status.as_of);
      s.add(id, war.intensity.as_of);
    }
    for (const e of war.events) {
      war.theatre.forEach((id) => s.add(id, e.date));
      s.addSources(war.theatre, e.date, e.sources);
    }
  }
  for (const l of w.leaders) {
    spanFacts(s, [l.country], l);
  }
  for (const m of w.memberships) {
    spanFacts(s, [m.member], m);
  }
  for (const x of w.sanctions) {
    spanFacts(s, [x.by, x.target], x);
  }
  for (const t of w.treaties) {
    spanFacts(s, t.parties, t);
  }
}

function controlFacts(m: StaleModel, c: ControlModel): void {
  for (const l of c.layers) {
    const days = [l.episode.from, l.episode.to].filter((d): d is DateStr => d !== null).map(dayOf);
    for (const day of days) {
      for (const h of l.hexes) {
        m.hexKeys.push(day * HEX_COUNT + h);
      }
    }
  }
  for (const s of c.series) {
    m.series.push({ days: Int32Array.from(s.series.snapshots, (x) => dayOf(x.date)), hexes: s.snaps.map((x) => x.hexes) });
  }
}

export function buildStale(inp: StaleInputs): StaleModel {
  const maxCountry = inp.actors.maxCountry;
  const m: StaleModel = {
    maxCountry,
    lists: Array.from({ length: maxCountry + 1 }, () => []),
    sorted: [],
    hexKeys: [],
    hexSorted: new Float64Array(0),
    dirty: true,
    series: [],
    scratch: new Int32Array(HEX_COUNT),
    version: 0,
    lut: levelTable(),
  };
  worldFacts(sink(m, inp.actors, inp.sources), inp.world);
  controlFacts(m, inp.control);
  return m;
}

/** Add Current events bullets (one loaded year). */
export function ingestEvents(m: StaleModel, actors: ActorTable, events: WorldEvent[]): void {
  const s = sink(m, actors, new Map());
  for (const e of events) {
    for (const id of e.c) {
      s.add(id, e.d);
    }
    if (e.hex !== undefined && e.hex >= 0 && e.hex < HEX_COUNT) {
      m.hexKeys.push(dayOf(e.d) * HEX_COUNT + e.hex);
    }
  }
  m.dirty = true;
  m.version++;
}

function prepare(m: StaleModel): void {
  if (!m.dirty) {
    return;
  }
  m.sorted = m.lists.map((l) => Int32Array.from(l).sort());
  m.hexSorted = Float64Array.from(m.hexKeys).sort();
  m.dirty = false;
}

// ─── at a date ───────────────────────────────────────────────────────────────────────────

function countryNewest(m: StaleModel, day: number): Int32Array {
  const out = new Int32Array(m.maxCountry + 1).fill(NEVER);
  m.sorted.forEach((days, id) => {
    const k = lastAtMost(days, day);
    if (k >= 0) {
      out[id] = days[k];
    }
  });
  return out;
}

/** Newest hex-level fact ≤ day per hex, into the scratch buffer. */
function hexNewest(m: StaleModel, day: number): Int32Array {
  const out = m.scratch.fill(NEVER);
  const limit = (day + 1) * HEX_COUNT;
  const keys = m.hexSorted;

  // Keys ascend by day, so a later key for the same hex simply overwrites.
  for (let i = 0; i < keys.length && keys[i] < limit; i++) {
    const k = keys[i];
    const hex = k % HEX_COUNT;
    out[hex] = (k - hex) / HEX_COUNT;
  }

  for (const s of m.series) {
    const k = lastAtMost(s.days, day);
    if (k < 0) {
      continue;
    }
    const snapDay = s.days[k];
    for (const h of s.hexes[k]) {
      out[h] = Math.max(out[h], snapDay);
    }
  }
  return out;
}

/** Staleness per hex at `date`, given the default-worldview owner at that date. A new array. */
export function stalenessAt(m: StaleModel, date: DateStr, owner: Uint16Array): Uint8Array {
  prepare(m);
  const day = dayOf(date);
  const byCountry = countryNewest(m, day);
  const byHex = hexNewest(m, day);
  const out = new Uint8Array(HEX_COUNT);

  for (let h = 0; h < HEX_COUNT; h++) {
    const o = owner[h];
    const c = o <= m.maxCountry ? byCountry[o] : NEVER;
    const newest = c > byHex[h] ? c : byHex[h];
    const age = day - newest;
    out[h] = age >= STALE_DAYS ? MAX_LEVEL : m.lut[age < 0 ? 0 : age];
  }
  return out;
}

/** `stale` with every hex of unknown control at full fog; `stale` itself when none is (it is shared, read-only). */
export function fogUnknown(stale: Uint8Array, basis: Uint8Array): Uint8Array {
  let out: Uint8Array | null = null;

  for (let h = 0; h < HEX_COUNT; h++) {
    if (basis[h] !== BASIS.UNKNOWN) {
      continue;
    }
    out ??= stale.slice();
    out[h] = MAX_LEVEL;
  }
  return out ?? stale;
}
